/**
 * Oturum kaydı (Tb_Rn_Sys_UserSessions) için istemci tarafı yardımcıları.
 *
 * Kayıt "kim, ne zaman, nereden girdi/çıktı" sorusunun cevabı: giriş geçmişi, tek-oturum
 * zorlaması ve başarısız giriş denetimi hep bu satırlara bakıyor.
 */

import { runtimeConfig } from '@/app/config/runtimeConfig';
import { fetchLocalDevice } from '@/shared/lib/octo/localDevice';
import { deviceTag } from './deviceIdentity';

/**
 * Giriş kaydındaki uygulama adı. Blazor kabuğu "Octo.Web" yazıyor; React arayüzü ondan
 * ayrılmazsa iki arayüzün kayıtları aynı ada karışıyor ve hangi arayüzden girildiği
 * anlaşılmıyor.
 */
export function sessionAppName(): string {
  return `${runtimeConfig().appId}.React`;
}

/** Boş GUID; sunucu oturum kimliğini doldurmadığında yanıta bu düşüyor. */
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Sunucudan gelen oturum kimliği kullanılabilir mi?
 *
 * Boş GUID "dolu bir metin" olduğu için `sessionId || crypto.randomUUID()` onu geçerli
 * sanıyordu: bütün girişler AYNI kimlikle kaydediliyor, sunucu da her seferinde aynı
 * satırı güncelliyordu — giriş geçmişi hiç birikmedi, tek-oturum zorlaması da
 * tetiklenmedi. Sunucu artık kimliği üretiyor; bu kontrol eski sürümlere karşı sigorta.
 */
export function isUsableSessionId(sessionId: string | null | undefined): boolean {
  return !!sessionId && sessionId !== EMPTY_GUID;
}

/** Kullanılabilir kimlik yoksa istemci kendi üretir. */
export function resolveSessionId(fromServer: string | null | undefined): string {
  return isUsableSessionId(fromServer) ? String(fromServer) : crypto.randomUUID();
}

/** Chromium'un User-Agent Client Hints arayüzü; diğer tarayıcılarda yok. */
interface UaDataValues {
  platform?: string;
  platformVersion?: string;
  model?: string;
  fullVersionList?: { brand: string; version: string }[];
}
interface NavigatorUaData {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UaDataValues>;
}

/** Tarayıcı motorunun kendi adı değil, kullanıcının tanıdığı marka (Chrome, Edge…). */
function realBrand(brands: { brand: string; version: string }[] | undefined) {
  // "Not;A=Brand" gibi kasıtlı çöp girdiler ve motor adları elenir.
  return brands?.find((b) => !/not.*a.*brand|chromium/i.test(b.brand));
}

/** User-Agent metninden tarayıcı ve işletim sistemi — Client Hints yoksa tek kaynak bu. */
function fromUserAgent(ua: string): string {
  const browser =
    /Edg\/([\d.]+)/.exec(ua)?.[0].replace('Edg/', 'Edge ') ??
    /Firefox\/([\d.]+)/.exec(ua)?.[0].replace('/', ' ') ??
    /Chrome\/([\d.]+)/.exec(ua)?.[0].replace('/', ' ') ??
    (/Safari\//.test(ua) ? `Safari ${/Version\/([\d.]+)/.exec(ua)?.[1] ?? ''}`.trim() : null);

  const os = /Windows NT ([\d.]+)/.test(ua)
    ? 'Windows'
    : /Mac OS X/.test(ua)
      ? 'macOS'
      : (/Android ([\d.]+)/.exec(ua)?.[0] ??
        (/iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : null));

  // Cihaz modeli yalnızca mobil UA'larda geçiyor; masaüstünde tarayıcı hiç vermiyor.
  const device = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Macintosh/.test(ua)
        ? 'Mac'
        : (/Android [\d.]+; ([^;)]+)/.exec(ua)?.[1]?.trim() ?? null);

  return [device, os, browser].filter(Boolean).join(' · ');
}

/**
 * Kayda yazılan istemci tanıtımı: cihaz · işletim sistemi · tarayıcı, sonunda ham
 * User-Agent.
 *
 * IP'yi SUNUCU ekliyor (istemcinin bildirdiği adrese güvenilemez).
 *
 * CİHAZ ADI (örn. "Taha'nın MacBook'u") tarayıcıdan ALINAMAZ — hiçbir web API'si makine
 * adını vermez, masaüstü uygulamalarının yazdığı ITHostName'in web karşılığı yok.
 * Alınabilenin en fazlası marka/model: Chromium tabanlı tarayıcılarda Client Hints ile
 * (mobilde model dolu gelir, masaüstünde boştur), diğerlerinde User-Agent metninden.
 */
export async function clientInfo(): Promise<string> {
  const ua = navigator.userAgent || '';
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUaData }).userAgentData;

  let summary = '';
  try {
    if (uaData?.getHighEntropyValues) {
      const hints = await uaData.getHighEntropyValues(['model', 'platformVersion', 'fullVersionList']);
      const brand = realBrand(hints.fullVersionList) ?? realBrand(uaData.brands);
      const platform = [hints.platform ?? uaData.platform, hints.platformVersion].filter(Boolean).join(' ');
      summary = [hints.model || null, platform || null, brand ? `${brand.brand} ${brand.version}` : null]
        .filter(Boolean)
        .join(' · ');
    }
  } catch {
    /* Client Hints reddedilebilir; User-Agent yedeği devrede */
  }

  if (!summary) summary = fromUserAgent(ua);

  // Cihaz etiketi MAC/hostname'in yerini tutar: aynı tarayıcıdan gelen girişler bu
  // kimlikle birbirine bağlanır (bkz. deviceIdentity). Bilgisayarda Octo.Agent varsa
  // cihaz kodu/makine adı da yazılır; ajan yoksa istek anında reddedilir, giriş beklemez.
  const local = await fetchLocalDevice();
  const parts = [summary, `Cihaz: ${deviceTag(local)}`, ua].filter(Boolean);
  return (parts.join(' · ') || 'Bilinmeyen tarayıcı').slice(0, 500);
}
